import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import type {
  BomItem,
  BomItemCostTiming,
  BomItemStatus,
  BomUnit,
  EquipmentCatalogEntry,
  SCMCategory,
  Supplier,
} from '../../../types/supplyChain';
import {
  SCM_CATEGORY_LABELS,
  recomputeTotalCost,
  isGenerationBomItem,
  bomItemDepositUsd,
} from '../../../types/supplyChain';
import { BOM_STATUS_ORDER, STATUS_LABELS } from '../../../lib/bomItemStatusFlow';
import { resolveEquipmentCatalog } from '../../../lib/resolveEquipmentCatalog';
import { catalogEntryToBomDraft } from '../../../lib/matchCatalogToBomItem';
import {
  BOM_CRITICALITY_FIELD_LABEL,
  BOM_CRITICALITY_OPTIONS,
  BOM_CRITICALITY_LABELS,
  BOM_SUBSTITUTABILITY_FIELD_LABEL,
  BOM_SUBSTITUTABILITY_OPTIONS,
  BOM_SUBSTITUTABILITY_LABELS,
} from '../../../lib/bomFieldLabels';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';

type Mode = 'add' | 'edit';

/**
 * Phase choice surfaced in the dialog's Phase <select> (A3). The caller
 * (SupplyChainPanel) resolves these from `effectivePhases(intent)` so a
 * manually-added line is bucketed into the user's real rollout phase instead
 * of being silently defaulted to Phase 1 downstream. Optional + defaults to
 * `[]` so the BoM-table caller (out of this wave's allowlist) keeps compiling;
 * with no phases the line's stored `phaseId` still round-trips verbatim.
 */
export interface PhaseChoice {
  id: string;
  label: string;
}

const INPUT_CLASS =
  'w-full rounded-sm border border-border bg-background px-2.5 py-2 text-[11px] text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40';
const READONLY_CLASS =
  'w-full rounded-sm border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-text-secondary';

/** USD formatter for the live readouts (no decimals — line items are coarse). */
const usd = (n: number) =>
  `$${Math.round(n).toLocaleString('en-US')}`;

function FormField({
  label,
  required,
  hint,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
        {label}
        {required ? ' *' : ''}
      </span>
      {hint && (
        <p className="text-[10px] leading-relaxed text-text-muted">{hint}</p>
      )}
      {children}
    </div>
  );
}

function FormSection({
  title,
  description,
  children,
  collapsible = false,
  open = true,
  onToggle,
  testId,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  testId?: string;
}) {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <section className="overflow-hidden rounded-sm border border-border/80 bg-background" data-testid={testId}>
      {collapsible ? (
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-start justify-between gap-3 border-b border-border/60 px-3 py-2.5 text-left hover:bg-muted/30"
        >
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-foreground">{title}</h3>
            {description && (
              <p className="mt-0.5 text-[10px] leading-relaxed text-text-muted">{description}</p>
            )}
          </div>
          <Chevron className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden />
        </button>
      ) : (
        <div className="border-b border-border/60 px-3 py-2.5">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-foreground">{title}</h3>
          {description && (
            <p className="mt-0.5 text-[10px] leading-relaxed text-text-muted">{description}</p>
          )}
        </div>
      )}
      {(!collapsible || open) && (
        <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2">{children}</div>
      )}
    </section>
  );
}

const CATEGORY_ORDER: SCMCategory[] = [
  'power-generation',
  'power-conversion',
  'energy-storage',
  'cooling',
  'data-center-it',
  'connectivity',
  'civil-construction',
  'logistics',
  'commissioning',
];

const UNITS: BomUnit[] = ['each', 'kVA', 'MW', 'MWh', 'm', 'm2', 'ton', 'm3', 'lot'];

interface BomItemFormState {
  category: SCMCategory;
  name: string;
  description: string;
  spec: string;
  quantity: string;
  unit: BomUnit;
  status: BomItemStatus;
  unitCostUsd: string;
  /** BOM-CAPEX (#28) — net MW per unit; blank for non-generation items. */
  powerPerUnitMw: string;
  leadTimeMonthsLow: string;
  leadTimeMonthsHigh: string;
  /** A3 — rollout phase this line belongs to. '' = unphased / Phase 1. */
  phaseId: string;
  // #29 per-line cost-timing — stored as form strings, parsed on submit.
  depositBasis: 'none' | 'pct-of-cost' | 'fixed-usd';
  depositPct: string;
  depositAmountUsd: string;
  orderLeadMonths: string;
  balanceTiming: 'at-delivery' | 'at-cod';
  criticality: BomItem['criticality'];
  substitutability: BomItem['substitutability'];
  complianceRequirements: string;
  shortlistedSupplierIds: string[];
  requiredOnSiteDate: string;
  expectedOnSiteDate: string;
  baselineDepositAmountUsd: string;
  baselinePaymentTerms: string;
  baselineFreightTerms: string;
  baselineQuoteValidityDays: string;
  notes: string;
}

type CatalogBackedMetadata = Pick<
  BomItem,
  | 'equipmentCatalogEntryId'
  | 'oemGenerationBasis'
  | 'oemElectricalMachineBasis'
  | 'priceSource'
  | 'leadTimeSource'
>;

function catalogMetadataFromItem(item?: BomItem): CatalogBackedMetadata | null {
  if (!item?.equipmentCatalogEntryId
    && !item?.oemGenerationBasis
    && !item?.oemElectricalMachineBasis) return null;
  return {
    equipmentCatalogEntryId: item.equipmentCatalogEntryId,
    oemGenerationBasis: item.oemGenerationBasis,
    oemElectricalMachineBasis: item.oemElectricalMachineBasis,
    priceSource: item.priceSource,
    leadTimeSource: item.leadTimeSource,
  };
}

function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** An unrelated edit must retain the original time and timezone of a stored date. */
function dateIsoForSave(value: string, originalIso?: string): string {
  if (originalIso && value === toDateInputValue(originalIso)) return originalIso;
  return new Date(value).toISOString();
}

function buildInitialState(item?: BomItem, defaultPhaseId?: string): BomItemFormState {
  const timing = item?.costTiming;
  return {
    category: item?.category ?? 'power-conversion',
    name: item?.name ?? '',
    description: item?.description ?? '',
    spec: item?.spec ?? '',
    quantity: String(item?.quantity ?? 1),
    unit: item?.unit ?? 'each',
    status: item?.status ?? 'planned',
    unitCostUsd: item?.unitCostUsd == null ? '' : String(item.unitCostUsd),
    powerPerUnitMw: item?.powerPerUnitMw == null ? '' : String(item.powerPerUnitMw),
    leadTimeMonthsLow: String(item?.leadTimeMonthsLow ?? 6),
    leadTimeMonthsHigh: String(item?.leadTimeMonthsHigh ?? 12),
    // A3 — round-trip the line's phase EXPLICITLY (never rely on {...item}); for a
    // brand-new line, seed from the caller's current phase context.
    phaseId: item?.phaseId ?? defaultPhaseId ?? '',
    depositBasis: timing?.depositBasis ?? 'none',
    depositPct: timing?.depositPct == null ? '' : String(timing.depositPct),
    depositAmountUsd: timing?.depositAmountUsd == null ? '' : String(timing.depositAmountUsd),
    orderLeadMonths: timing?.orderLeadMonths == null ? '' : String(timing.orderLeadMonths),
    balanceTiming: timing?.balanceTiming ?? 'at-delivery',
    criticality: item?.criticality ?? 'standard',
    substitutability: item?.substitutability ?? 'commodity',
    complianceRequirements: (item?.complianceRequirements ?? []).join(', '),
    shortlistedSupplierIds: item?.shortlistedSupplierIds ?? [],
    requiredOnSiteDate: toDateInputValue(item?.requiredOnSiteDate),
    expectedOnSiteDate: toDateInputValue(item?.expectedOnSiteDate),
    baselineDepositAmountUsd: item?.baselineDepositAmountUsd == null ? '' : String(item.baselineDepositAmountUsd),
    baselinePaymentTerms: item?.baselinePaymentTerms ?? '',
    baselineFreightTerms: item?.baselineFreightTerms ?? '',
    baselineQuoteValidityDays: item?.baselineQuoteValidityDays == null ? '' : String(item.baselineQuoteValidityDays),
    notes: item?.notes ?? '',
  };
}

export interface BomItemEditDialogProps {
  open: boolean;
  mode: Mode;
  initialItem?: BomItem;
  suppliers: Supplier[];
  /** Owning tenant used to resolve private products and commercial patches. */
  companyId?: string | null;
  /**
   * A3 — rollout phases the line can be assigned to. Optional + defaults to
   * `[]` (no Phase select rendered, stored phaseId still round-trips). The
   * SupplyChainPanel caller resolves these from `effectivePhases(intent)`.
   */
  phases?: PhaseChoice[];
  /** Phase to seed a NEW line with (the caller's current phase context). */
  defaultPhaseId?: string;
  onCreateSupplier?: (supplier: Pick<Supplier, 'name' | 'hq' | 'categories'>) => Promise<string | null>;
  /**
   * B3 — promote an inline-created site-local supplier to the company
   * directory so it can be reused across sites. Optional: when absent the
   * "save to company directory" affordance is hidden. Returns true on success.
   */
  onSaveSupplierToCompany?: (supplier: Supplier) => Promise<boolean>;
  onCancel: () => void;
  onSubmit: (payload: Omit<BomItem, 'id' | 'derivedFrom'>) => void;
}

export const BomItemEditDialog: React.FC<BomItemEditDialogProps> = ({
  open,
  mode,
  initialItem,
  suppliers,
  companyId,
  phases = [],
  defaultPhaseId,
  onCreateSupplier,
  onSaveSupplierToCompany,
  onCancel,
  onSubmit,
}) => {
  const [form, setForm] = useState<BomItemFormState>(() => buildInitialState(initialItem, defaultPhaseId));
  const [error, setError] = useState<string | null>(null);
  const [entryMode, setEntryMode] = useState<'manual' | 'catalog'>('manual');
  const [catalogEntries, setCatalogEntries] = useState<EquipmentCatalogEntry[]>([]);
  const [catalogSupplierId, setCatalogSupplierId] = useState('');
  const [catalogItemId, setCatalogItemId] = useState('');
  const [catalogMetadata, setCatalogMetadata] = useState<CatalogBackedMetadata | null>(
    () => catalogMetadataFromItem(initialItem),
  );
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierCountry, setNewSupplierCountry] = useState('');
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  // B3 — id of an inline-created supplier eligible to be promoted to the
  // company directory, plus busy/done flags for the promote affordance.
  const [promotableSupplierId, setPromotableSupplierId] = useState<string | null>(null);
  const [promotingSupplier, setPromotingSupplier] = useState(false);
  const [promotedSupplierIds, setPromotedSupplierIds] = useState<readonly string[]>([]);
  // C4 — everything past the always-open core lives behind one "Advanced" toggle.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(buildInitialState(initialItem, defaultPhaseId));
      setError(null);
      setEntryMode('manual');
      setCatalogSupplierId('');
      setCatalogItemId('');
      setCatalogMetadata(catalogMetadataFromItem(initialItem));
      setNewSupplierName('');
      setNewSupplierCountry('');
      setPromotableSupplierId(null);
      setPromotingSupplier(false);
      setPromotedSupplierIds([]);
      setAdvancedOpen(false);
    }
  }, [open, initialItem, defaultPhaseId]);

  useEffect(() => {
    if (!open || mode !== 'add') return;
    void resolveEquipmentCatalog(companyId)
      .then(setCatalogEntries)
      .catch(() => setCatalogEntries([]));
  }, [companyId, open, mode]);

  const shortlistedSet = useMemo(() => new Set(form.shortlistedSupplierIds), [form.shortlistedSupplierIds]);
  const supplierCatalogEntries = useMemo(() => {
    if (!catalogSupplierId) return [] as EquipmentCatalogEntry[];
    const supplier = suppliers.find(s => s.id === catalogSupplierId);
    const local = supplier?.catalog ?? [];
    const global = catalogEntries.filter(e => (e.supplierIds ?? []).includes(catalogSupplierId));
    const byId = new Map<string, EquipmentCatalogEntry>();
    for (const item of [...local, ...global]) byId.set(item.id, item);
    return Array.from(byId.values());
  }, [catalogEntries, catalogSupplierId, suppliers]);

  if (!open) return null;

  const setField = <K extends keyof BomItemFormState>(key: K, value: BomItemFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const prefillFromCatalog = () => {
    const selected = supplierCatalogEntries.find(e => e.id === catalogItemId);
    if (!selected) {
      setError('Select a supplier catalog item first.');
      return;
    }
    const draft = catalogEntryToBomDraft(selected);
    setCatalogMetadata({
      equipmentCatalogEntryId: draft.equipmentCatalogEntryId,
      oemGenerationBasis: draft.oemGenerationBasis,
      oemElectricalMachineBasis: draft.oemElectricalMachineBasis,
      priceSource: draft.priceSource,
      leadTimeSource: draft.leadTimeSource,
    });
    setForm(prev => ({
      ...prev,
      category: draft.category,
      name: draft.name,
      description: draft.description ?? prev.description,
      spec: draft.spec,
      quantity: String(draft.quantity),
      unit: draft.unit,
      status: draft.status,
      unitCostUsd: draft.unitCostUsd == null ? '' : String(draft.unitCostUsd),
      leadTimeMonthsLow: String(draft.leadTimeMonthsLow),
      leadTimeMonthsHigh: String(draft.leadTimeMonthsHigh),
      powerPerUnitMw: draft.powerPerUnitMw == null ? '' : String(draft.powerPerUnitMw),
      notes: draft.notes ?? prev.notes,
      shortlistedSupplierIds: Array.from(new Set([
        ...(prev.shortlistedSupplierIds ?? []),
        ...(draft.shortlistedSupplierIds ?? []),
        ...(catalogSupplierId ? [catalogSupplierId] : []),
      ])),
    }));
    setError(null);
  };

  const continueWithManualForSupplier = () => {
    if (!catalogSupplierId) return;
    setForm(prev => ({
      ...prev,
      shortlistedSupplierIds: Array.from(new Set([...(prev.shortlistedSupplierIds ?? []), catalogSupplierId])),
    }));
    setEntryMode('manual');
    setError(null);
  };

  const createSupplierInline = async () => {
    if (!onCreateSupplier) return;
    const name = newSupplierName.trim();
    const country = newSupplierCountry.trim();
    if (!name || !country) {
      setError('Supplier name and country are required.');
      return;
    }
    setCreatingSupplier(true);
    const id = await onCreateSupplier({
      name,
      hq: { country },
      categories: [form.category],
    });
    setCreatingSupplier(false);
    if (!id) {
      setError('Could not create supplier.');
      return;
    }
    setCatalogSupplierId(id);
    setForm(prev => ({
      ...prev,
      shortlistedSupplierIds: Array.from(new Set([...(prev.shortlistedSupplierIds ?? []), id])),
    }));
    setEntryMode('catalog');
    // B3 — remember the just-created supplier so it can optionally be promoted
    // to the reusable company directory.
    setPromotableSupplierId(id);
    setNewSupplierName('');
    setNewSupplierCountry('');
    setError(null);
  };

  const promoteSupplierToCompany = async () => {
    if (!onSaveSupplierToCompany || !promotableSupplierId) return;
    const supplier = suppliers.find(s => s.id === promotableSupplierId);
    if (!supplier) return;
    setPromotingSupplier(true);
    const ok = await onSaveSupplierToCompany(supplier);
    setPromotingSupplier(false);
    if (ok) {
      setPromotedSupplierIds(prev => Array.from(new Set([...prev, supplier.id])));
    } else {
      setError('Could not save supplier to company directory.');
    }
  };

  const toggleShortlisted = (supplierId: string) => {
    setForm(prev => {
      const next = new Set(prev.shortlistedSupplierIds);
      if (next.has(supplierId)) next.delete(supplierId);
      else next.add(supplierId);
      return {
        ...prev,
        shortlistedSupplierIds: Array.from(next),
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    // Explicitly send an empty string when clearing a saved description: the
    // normal item updater merges fields, so omitting it would retain old text.
    const description = form.description === (initialItem?.description ?? '')
      ? initialItem?.description
      : form.description.trim();
    const quantity = Number(form.quantity);
    const leadLow = Number(form.leadTimeMonthsLow);
    const leadHigh = Number(form.leadTimeMonthsHigh);
    const parsedUnitCost = form.unitCostUsd.trim() === '' ? null : Number(form.unitCostUsd);
    const parsedPowerPerUnit = form.powerPerUnitMw.trim() === '' ? null : Number(form.powerPerUnitMw);
    const baselineDeposit = form.baselineDepositAmountUsd.trim() === '' ? null : Number(form.baselineDepositAmountUsd);
    const baselineValidity = form.baselineQuoteValidityDays.trim() === '' ? null : Number(form.baselineQuoteValidityDays);
    // #29 — parse the cost-timing inputs.
    const parsedDepositPct = form.depositPct.trim() === '' ? null : Number(form.depositPct);
    const parsedDepositAmount = form.depositAmountUsd.trim() === '' ? null : Number(form.depositAmountUsd);
    const parsedOrderLead = form.orderLeadMonths.trim() === '' ? null : Number(form.orderLeadMonths);

    if (!name) {
      setError('Equipment name is required.');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }
    if (!Number.isFinite(leadLow) || leadLow < 0) {
      setError('Lead-time low must be zero or greater.');
      return;
    }
    if (!Number.isFinite(leadHigh) || leadHigh < leadLow) {
      setError('Lead-time high must be greater than or equal to lead-time low.');
      return;
    }
    if (parsedUnitCost != null && (!Number.isFinite(parsedUnitCost) || parsedUnitCost < 0)) {
      setError('Unit cost must be a positive number when provided.');
      return;
    }
    if (parsedPowerPerUnit != null && (!Number.isFinite(parsedPowerPerUnit) || parsedPowerPerUnit < 0)) {
      setError('Power per unit must be a positive number when provided.');
      return;
    }
    if (baselineDeposit != null && (!Number.isFinite(baselineDeposit) || baselineDeposit < 0)) {
      setError('Baseline deposit must be a positive number when provided.');
      return;
    }
    if (baselineValidity != null && (!Number.isFinite(baselineValidity) || baselineValidity < 0)) {
      setError('Baseline quote validity must be a positive number when provided.');
      return;
    }
    // #29 — validate timing inputs against their selected basis.
    if (form.depositBasis === 'pct-of-cost' && parsedDepositPct != null && (!Number.isFinite(parsedDepositPct) || parsedDepositPct < 0 || parsedDepositPct > 100)) {
      setError('Deposit % must be between 0 and 100.');
      return;
    }
    if (form.depositBasis === 'fixed-usd' && parsedDepositAmount != null && (!Number.isFinite(parsedDepositAmount) || parsedDepositAmount < 0)) {
      setError('Deposit amount must be a positive number.');
      return;
    }
    if (parsedOrderLead != null && (!Number.isFinite(parsedOrderLead) || parsedOrderLead < 0)) {
      setError('Order lead (months) must be zero or greater.');
      return;
    }

    const complianceRequirements = form.complianceRequirements
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);

    // #29 — assemble costTiming ONLY when a deposit basis is selected. Omit the
    // whole object otherwise so untimed lines stay byte-for-byte legacy.
    const costTiming: BomItemCostTiming | undefined =
      form.depositBasis === 'none'
        ? undefined
        : {
            depositBasis: form.depositBasis,
            ...(form.depositBasis === 'pct-of-cost' && parsedDepositPct != null ? { depositPct: parsedDepositPct } : {}),
            ...(form.depositBasis === 'fixed-usd' && parsedDepositAmount != null ? { depositAmountUsd: parsedDepositAmount } : {}),
            ...(parsedOrderLead != null ? { orderLeadMonths: parsedOrderLead } : {}),
            balanceTiming: form.balanceTiming,
          };

    const payload: Omit<BomItem, 'id' | 'derivedFrom'> = {
      category: form.category,
      name,
      ...(description !== undefined ? { description } : {}),
      spec: form.spec.trim(),
      quantity,
      unit: form.unit,
      status: form.status,
      unitCostUsd: parsedUnitCost,
      totalCostUsd: recomputeTotalCost({ quantity, unitCostUsd: parsedUnitCost }),
      ...(parsedPowerPerUnit != null && parsedPowerPerUnit > 0 ? { powerPerUnitMw: parsedPowerPerUnit } : {}),
      priceSource: catalogMetadata?.priceSource
        ?? (initialItem && parsedUnitCost === (initialItem.unitCostUsd ?? null)
          ? initialItem.priceSource
          : (parsedUnitCost == null ? 'industry-baseline' : 'manual')),
      leadTimeMonthsLow: leadLow,
      leadTimeMonthsHigh: leadHigh,
      // Editing scope/criticality must not turn an unchanged unknown or
      // document-backed lead range into a new manual scheduling authority.
      leadTimeSource: catalogMetadata?.leadTimeSource
        ?? (initialItem
          && leadLow === initialItem.leadTimeMonthsLow
          && leadHigh === initialItem.leadTimeMonthsHigh
          ? initialItem.leadTimeSource
          : 'manual'),
      ...(catalogMetadata?.equipmentCatalogEntryId
        ? { equipmentCatalogEntryId: catalogMetadata.equipmentCatalogEntryId }
        : {}),
      ...(catalogMetadata?.oemGenerationBasis
        ? { oemGenerationBasis: catalogMetadata.oemGenerationBasis }
        : {}),
      ...(catalogMetadata?.oemElectricalMachineBasis
        ? { oemElectricalMachineBasis: catalogMetadata.oemElectricalMachineBasis }
        : {}),
      // A3 — round-trip phaseId EXPLICITLY (omit when unset so unphased lines
      // stay undefined, matching the legacy single-phase contract).
      ...(form.phaseId ? { phaseId: form.phaseId } : {}),
      ...(costTiming ? { costTiming } : {}),
      shortlistedSupplierIds: form.shortlistedSupplierIds,
      // C5 — `awardedSupplierId` is no longer authored here; award is the quote
      // pipeline's job. We preserve any pre-existing awarded supplier on the
      // item only if it's still shortlisted, but never set a new one from this
      // dialog.
      ...(initialItem?.awardedSupplierId && form.shortlistedSupplierIds.includes(initialItem.awardedSupplierId)
        ? { awardedSupplierId: initialItem.awardedSupplierId }
        : {}),
      ...(complianceRequirements.length > 0 ? { complianceRequirements } : {}),
      criticality: form.criticality,
      substitutability: form.substitutability,
      ...(form.requiredOnSiteDate ? { requiredOnSiteDate: dateIsoForSave(form.requiredOnSiteDate, initialItem?.requiredOnSiteDate) } : {}),
      ...(form.expectedOnSiteDate ? { expectedOnSiteDate: dateIsoForSave(form.expectedOnSiteDate, initialItem?.expectedOnSiteDate) } : {}),
      ...(baselineDeposit != null ? { baselineDepositAmountUsd: baselineDeposit } : {}),
      ...(form.baselinePaymentTerms.trim() ? { baselinePaymentTerms: form.baselinePaymentTerms.trim() } : {}),
      ...(form.baselineFreightTerms.trim() ? { baselineFreightTerms: form.baselineFreightTerms.trim() } : {}),
      ...(baselineValidity != null ? { baselineQuoteValidityDays: baselineValidity } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
    onSubmit(payload);
  };

  const parsedQty = Number(form.quantity);
  const parsedUnitCostPreview = form.unitCostUsd.trim() === '' ? null : Number(form.unitCostUsd);
  const qtyValid = Number.isFinite(parsedQty) && parsedQty > 0;
  const unitCostValid = parsedUnitCostPreview != null && Number.isFinite(parsedUnitCostPreview);
  const extendedPricePreview = qtyValid && unitCostValid
    ? recomputeTotalCost({ quantity: parsedQty, unitCostUsd: parsedUnitCostPreview })
    : null;
  // C1 — self-documenting "qty × unit cost = line total" readout. Unit cost is
  // PER SINGLE UNIT; the total is qty × unit. Before a cost is entered the
  // unit slot shows "$—" so the formula is always legible.
  const qtyLabel = qtyValid ? String(parsedQty) : (form.quantity.trim() === '' ? '—' : form.quantity);
  const unitCostLabel = unitCostValid ? usd(parsedUnitCostPreview as number) : '$—';
  const lineTotalLabel = extendedPricePreview != null ? usd(extendedPricePreview) : '$—';
  const extendedPriceFormula = `${qtyLabel} × ${unitCostLabel} = ${lineTotalLabel}`;

  // BOM-CAPEX (#28) — power-per-unit is only meaningful for generation items.
  const isGenerationItem = isGenerationBomItem({ category: form.category });
  const parsedPowerPreview = form.powerPerUnitMw.trim() === '' ? null : Number(form.powerPerUnitMw);
  // Derived total power = quantity × MW/unit (MW). Null unless both are valid.
  const totalPowerMwPreview =
    qtyValid && parsedPowerPreview != null && Number.isFinite(parsedPowerPreview) && parsedPowerPreview > 0
      ? parsedQty * parsedPowerPreview
      : null;

  // #29 — live PER-LINE deposit/balance readout. Plain const (NOT useMemo — this
  // runs after the `if (!open) return null` early return, so a hook here would
  // break the Rules of Hooks; the computation is trivial). Resolve through the
  // shared `bomItemDepositUsd` helper so the dialog and Finance agree on the
  // basis/clamp logic exactly.
  const computeDepositPreview = (): { deposit: number; balance: number } | null => {
    if (form.depositBasis === 'none' || extendedPricePreview == null) return null;
    const parsedPct = form.depositPct.trim() === '' ? undefined : Number(form.depositPct);
    const parsedAmt = form.depositAmountUsd.trim() === '' ? undefined : Number(form.depositAmountUsd);
    const probe: BomItem = {
      id: '__preview__',
      category: form.category,
      name: form.name,
      spec: form.spec,
      quantity: parsedQty,
      unit: form.unit,
      status: form.status,
      unitCostUsd: parsedUnitCostPreview,
      totalCostUsd: extendedPricePreview,
      priceSource: 'manual',
      leadTimeMonthsLow: 0,
      leadTimeMonthsHigh: 0,
      leadTimeSource: 'manual',
      criticality: form.criticality,
      substitutability: form.substitutability,
      derivedFrom: 'manual',
      costTiming: {
        depositBasis: form.depositBasis,
        ...(parsedPct != null && Number.isFinite(parsedPct) ? { depositPct: parsedPct } : {}),
        ...(parsedAmt != null && Number.isFinite(parsedAmt) ? { depositAmountUsd: parsedAmt } : {}),
        balanceTiming: form.balanceTiming,
      },
    };
    const deposit = bomItemDepositUsd(probe);
    const balance = Math.max(0, extendedPricePreview - deposit);
    return { deposit, balance };
  };
  const depositPreview = computeDepositPreview();

  const balanceTimingLabel = form.balanceTiming === 'at-cod' ? 'at COD' : 'at delivery';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-label="BoM item editor"
      onClick={onCancel}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[min(90vh,880px)] w-full max-w-2xl flex-col overflow-hidden rounded-sm border border-border bg-background shadow-2xl"
        data-testid="scm-bom-edit-dialog"
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-foreground">
              {mode === 'add' ? 'Add BoM item' : 'Edit BoM item'}
            </h2>
            <p className="mt-1 text-[10px] leading-relaxed text-text-muted">
              Core equipment, pricing, phase and cash-timing up front; risk, compliance, suppliers and commercial baselines live in Advanced.
            </p>
          </div>
          <button type="button" onClick={onCancel} className="rounded-sm p-1 text-text-muted hover:bg-muted/50 hover:text-text-primary" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {mode === 'add' && (
            <section className="rounded-sm border border-border/80 bg-muted/10 p-3 space-y-2" data-testid="scm-bom-add-workflow">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">How would you like to add this item?</div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setEntryMode('manual')} className={cn('rounded-sm border px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider', entryMode === 'manual' ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border text-text-secondary hover:bg-muted/40')} data-testid="scm-bom-entry-manual">Enter manually</button>
                <button type="button" onClick={() => setEntryMode('catalog')} className={cn('rounded-sm border px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider', entryMode === 'catalog' ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border text-text-secondary hover:bg-muted/40')} data-testid="scm-bom-entry-catalog">Use supplier catalog</button>
              </div>
              {entryMode === 'catalog' && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <select value={catalogSupplierId} onChange={(e) => { setCatalogSupplierId(e.target.value); setCatalogItemId(''); }} className={INPUT_CLASS} data-testid="scm-bom-catalog-supplier">
                    <option value="">Select supplier</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <select value={catalogItemId} onChange={(e) => setCatalogItemId(e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-catalog-item">
                    <option value="">Select supplier item</option>
                    {supplierCatalogEntries.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                  {catalogSupplierId && supplierCatalogEntries.length === 0 && (
                    <div className="sm:col-span-2 rounded-sm border border-dashed border-border p-3" data-testid="scm-bom-catalog-empty-helper">
                      <div className="text-[10px] text-text-secondary">No catalog items found for this supplier yet.</div>
                      <button type="button" onClick={continueWithManualForSupplier} className="mt-2 rounded-sm border border-primary/40 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary hover:bg-primary/10" data-testid="scm-bom-catalog-empty-create-manual">Create item for this supplier</button>
                    </div>
                  )}
                  <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                    <button type="button" onClick={prefillFromCatalog} className="rounded-sm border border-primary/40 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary hover:bg-primary/10" data-testid="scm-bom-catalog-prefill">Prefill from catalog</button>
                    <span className="text-[10px] text-text-muted">or create supplier:</span>
                    <input value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} placeholder="Supplier name" className={cn(INPUT_CLASS, 'max-w-[10rem]')} data-testid="scm-bom-new-supplier-name" />
                    <input value={newSupplierCountry} onChange={(e) => setNewSupplierCountry(e.target.value)} placeholder="Country" className={cn(INPUT_CLASS, 'max-w-[8rem]')} data-testid="scm-bom-new-supplier-country" />
                    <button type="button" onClick={() => { void createSupplierInline(); }} disabled={!onCreateSupplier || creatingSupplier} className="rounded-sm border border-border px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-secondary hover:bg-muted/40 disabled:opacity-60" data-testid="scm-bom-new-supplier-create">{creatingSupplier ? 'Creating…' : 'Add supplier'}</button>
                  </div>
                  {/* B3 — once an inline supplier is created, offer to save it to the
                      reusable company directory (deterministic company-<id>-<slug>). */}
                  {promotableSupplierId && onSaveSupplierToCompany && (
                    <div className="sm:col-span-2 flex flex-wrap items-center gap-2 rounded-sm border border-dashed border-primary/30 bg-primary/5 p-2.5" data-testid="scm-bom-promote-supplier">
                      <span className="text-[10px] text-text-secondary">
                        Reuse this supplier across sites?
                      </span>
                      {promotedSupplierIds.includes(promotableSupplierId) ? (
                        <span className="text-[10px] font-semibold text-primary" data-testid="scm-bom-promote-supplier-done">Saved to company directory.</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { void promoteSupplierToCompany(); }}
                          disabled={promotingSupplier}
                          className="rounded-sm border border-primary/40 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary hover:bg-primary/10 disabled:opacity-60"
                          data-testid="scm-bom-promote-supplier-save"
                        >
                          {promotingSupplier ? 'Saving…' : 'Save to company directory'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          <FormSection title="Equipment" description="Identity, spec, and procurement classification.">
            <FormField label="Equipment name" required className="sm:col-span-2">
              <input value={form.name} onChange={(e) => setField('name', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-name" />
            </FormField>
            <FormField label="Description" hint="Purpose or scope of this equipment. Use technical detail for ratings." className="sm:col-span-2">
              <textarea aria-label="Description" rows={2} value={form.description} onChange={(e) => setField('description', e.target.value)} className={cn(INPUT_CLASS, 'resize-y')} data-testid="scm-bom-field-description" />
            </FormField>
            <FormField label="Category" required>
              <select value={form.category} onChange={(e) => setField('category', e.target.value as SCMCategory)} className={INPUT_CLASS} data-testid="scm-bom-field-category">
                {CATEGORY_ORDER.map(c => <option key={c} value={c}>{SCM_CATEGORY_LABELS[c]}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select value={form.status} onChange={(e) => setField('status', e.target.value as BomItemStatus)} className={INPUT_CLASS} data-testid="scm-bom-field-status">
                {BOM_STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </FormField>
            <FormField label="Spec / technical detail" className="sm:col-span-2">
              <textarea value={form.spec} onChange={(e) => setField('spec', e.target.value)} className={cn(INPUT_CLASS, 'min-h-[4.5rem] resize-y')} data-testid="scm-bom-field-spec" />
            </FormField>
          </FormSection>

          <FormSection title="Quantity & pricing" description="Unit cost is per SINGLE unit; line total = quantity × unit cost.">
            <FormField label="Quantity" required>
              <input type="number" min={0} step="any" value={form.quantity} onChange={(e) => setField('quantity', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-quantity" />
            </FormField>
            <FormField label="Unit" required>
              <select value={form.unit} onChange={(e) => setField('unit', e.target.value as BomUnit)} className={INPUT_CLASS} data-testid="scm-bom-field-unit">
                {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </FormField>
            <FormField label="Unit cost (USD, per single unit)">
              <input type="number" min={0} step="any" value={form.unitCostUsd} onChange={(e) => setField('unitCostUsd', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-unit-cost" />
            </FormField>
            <FormField label="Total extended price (USD)">
              <input value={extendedPricePreview == null ? '' : String(extendedPricePreview)} readOnly className={READONLY_CLASS} data-testid="scm-bom-field-extended-price" />
            </FormField>
            {/* C1 — always-visible, self-documenting line-total formula so the
                qty-vs-line-total ambiguity is impossible to misread. */}
            <div className="sm:col-span-2 rounded-sm border border-border/60 bg-muted/20 px-2.5 py-1.5 text-[10px] text-text-secondary" data-testid="scm-bom-line-total-readout">
              <span className="font-semibold uppercase tracking-wider text-text-muted">Line total</span>
              <span className="ml-2 font-mono text-foreground">{extendedPriceFormula}</span>
            </div>
            {isGenerationItem && (
              <>
                <FormField
                  label="Power per unit (MW)"
                  hint="Net electrical MW one unit contributes. Total capacity = quantity × MW/unit."
                >
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={form.powerPerUnitMw}
                    onChange={(e) => setField('powerPerUnitMw', e.target.value)}
                    className={INPUT_CLASS}
                    data-testid="scm-bom-field-power-per-unit"
                  />
                </FormField>
                <FormField label="Total power (MW)">
                  <input
                    value={totalPowerMwPreview == null ? '' : String(totalPowerMwPreview)}
                    readOnly
                    className={READONLY_CLASS}
                    data-testid="scm-bom-field-total-power"
                  />
                </FormField>
              </>
            )}
          </FormSection>

          <FormSection title="Schedule" description="Procurement lead time (months).">
            <FormField label="Lead time low (mo)" required>
              <input type="number" min={0} step="any" value={form.leadTimeMonthsLow} onChange={(e) => setField('leadTimeMonthsLow', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-lead-low" />
            </FormField>
            <FormField label="Lead time high (mo)" required>
              <input type="number" min={0} step="any" value={form.leadTimeMonthsHigh} onChange={(e) => setField('leadTimeMonthsHigh', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-lead-high" />
            </FormField>
          </FormSection>

          {/* A3 — Phase assignment. Only rendered when the caller supplies the
              project's phases; otherwise the line's stored phaseId still
              round-trips untouched through buildInitialState/handleSubmit. */}
          {phases.length > 0 && (
            <FormSection title="Phase" description="Which rollout phase this line belongs to. Drives per-phase CAPEX and critical-path timing.">
              <FormField label="Rollout phase" className="sm:col-span-2" hint="Manually-added lines are otherwise bucketed into Phase 1 downstream — set this explicitly.">
                <select value={form.phaseId} onChange={(e) => setField('phaseId', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-phase">
                  <option value="">Unphased (Phase 1)</option>
                  {phases.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </FormField>
            </FormSection>
          )}

          {/* #29 — per-line deposit + cash-timing. Deposit resolves PER LINE via
              bomItemDepositUsd; the readout makes the split explicit. */}
          <FormSection title="Deposit & cash timing" description="Per-line deposit on order + balance on delivery/COD. Lets Finance build a CAPEX drawdown curve.">
            <FormField label="Deposit basis">
              <select value={form.depositBasis} onChange={(e) => setField('depositBasis', e.target.value as BomItemFormState['depositBasis'])} className={INPUT_CLASS} data-testid="scm-bom-field-deposit-basis">
                <option value="none">No deposit (full cost at delivery)</option>
                <option value="pct-of-cost">% of line cost</option>
                <option value="fixed-usd">Fixed USD amount</option>
              </select>
            </FormField>
            {form.depositBasis === 'pct-of-cost' && (
              <FormField label="Deposit (% of line total)" hint="0–100. Applied to quantity × unit cost.">
                {/* No native max=100 — let the JS validation surface the clear
                    "between 0 and 100" message instead of a silent HTML5 block. */}
                <input type="number" min={0} step="any" value={form.depositPct} onChange={(e) => setField('depositPct', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-deposit-pct" />
              </FormField>
            )}
            {form.depositBasis === 'fixed-usd' && (
              <FormField label="Deposit amount (USD)">
                <input type="number" min={0} step="any" value={form.depositAmountUsd} onChange={(e) => setField('depositAmountUsd', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-deposit-amount" />
              </FormField>
            )}
            {form.depositBasis !== 'none' && (
              <>
                <FormField label="Order lead (months before on-site)" hint="Deposit cash leaves this many months before the delivery date.">
                  <input type="number" min={0} step="1" value={form.orderLeadMonths} onChange={(e) => setField('orderLeadMonths', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-order-lead" />
                </FormField>
                <FormField label="Balance paid">
                  <select value={form.balanceTiming} onChange={(e) => setField('balanceTiming', e.target.value as BomItemFormState['balanceTiming'])} className={INPUT_CLASS} data-testid="scm-bom-field-balance-timing">
                    <option value="at-delivery">At delivery / on-site</option>
                    <option value="at-cod">At COD (retention / holdback)</option>
                  </select>
                </FormField>
                {/* Live per-line deposit/balance summary. */}
                <div className="sm:col-span-2 rounded-sm border border-border/60 bg-muted/20 px-2.5 py-1.5 text-[10px] text-text-secondary" data-testid="scm-bom-deposit-readout">
                  {depositPreview == null ? (
                    <span>Enter a unit cost to see the per-line deposit / balance split.</span>
                  ) : (
                    <span>
                      Per line: <span className="font-semibold text-foreground">{usd(depositPreview.deposit)}</span> deposit on order,{' '}
                      <span className="font-semibold text-foreground">{usd(depositPreview.balance)}</span> balance {balanceTimingLabel}.
                    </span>
                  )}
                </div>
              </>
            )}
          </FormSection>

          {/* C4 — single Advanced disclosure holding everything past the core. */}
          <FormSection
            title="Advanced"
            description="Risk, compliance, suppliers, on-site dates and commercial baselines."
            collapsible
            open={advancedOpen}
            onToggle={() => setAdvancedOpen(v => !v)}
            testId="scm-bom-advanced-section"
          >
            <FormField
              label={BOM_CRITICALITY_FIELD_LABEL}
              required
              hint="How strongly this item affects project schedule and COD."
            >
              <select value={form.criticality} onChange={(e) => setField('criticality', e.target.value as BomItem['criticality'])} className={INPUT_CLASS} data-testid="scm-bom-field-criticality">
                {BOM_CRITICALITY_OPTIONS.map((value) => (
                  <option key={value} value={value}>{BOM_CRITICALITY_LABELS[value]}</option>
                ))}
              </select>
            </FormField>
            <FormField
              label={BOM_SUBSTITUTABILITY_FIELD_LABEL}
              required
              hint="How many qualified suppliers can deliver this equipment."
            >
              <select value={form.substitutability} onChange={(e) => setField('substitutability', e.target.value as BomItem['substitutability'])} className={INPUT_CLASS} data-testid="scm-bom-field-substitutability">
                {BOM_SUBSTITUTABILITY_OPTIONS.map((value) => (
                  <option key={value} value={value}>{BOM_SUBSTITUTABILITY_LABELS[value]}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Compliance requirements" className="sm:col-span-2">
              <input value={form.complianceRequirements} onChange={(e) => setField('complianceRequirements', e.target.value)} placeholder="Comma-separated certification ids" className={INPUT_CLASS} data-testid="scm-bom-field-compliance" />
            </FormField>
            <FormField label="Shortlisted suppliers" className="sm:col-span-2">
              <div className="max-h-28 overflow-auto rounded-sm border border-border p-2 space-y-1" data-testid="scm-bom-field-shortlist">
                {suppliers.length === 0 ? (
                  <div className="text-[10px] text-text-secondary">No suppliers available yet.</div>
                ) : suppliers.map(s => (
                  <label key={s.id} className="flex cursor-pointer items-center gap-2 text-[10px] text-text-secondary">
                    <input type="checkbox" checked={shortlistedSet.has(s.id)} onChange={() => toggleShortlisted(s.id)} />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
              {/* C5 — award is the quote pipeline's job; no manual award select here. */}
              <p className="mt-1 text-[10px] leading-relaxed text-text-muted">
                Award is set automatically when a quote is awarded on the Quotes tab — not here.
              </p>
            </FormField>
            <FormField label="Required on-site date">
              <input type="date" value={form.requiredOnSiteDate} onChange={(e) => setField('requiredOnSiteDate', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-required-date" />
            </FormField>
            <FormField label="Expected on-site date">
              <input type="date" value={form.expectedOnSiteDate} onChange={(e) => setField('expectedOnSiteDate', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-expected-date" />
            </FormField>
            {/* C2 — trimmed commercial baseline: only the 4 fields that
                computeCommercialTermsSyncFromBom actually reads. The 7 dead
                fields (delivery-window start/end, cert confirmed + notes,
                production-slot status + notes, capacity-availability status +
                notes) had zero consumers and were removed. */}
            <FormField label="Baseline deposit amount (USD)" hint="Display-only baseline; the per-line Deposit & cash timing above drives Finance.">
              <input type="number" min={0} step="any" value={form.baselineDepositAmountUsd} onChange={(e) => setField('baselineDepositAmountUsd', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-baseline-deposit" />
            </FormField>
            <FormField label="Quote validity (days)">
              <input type="number" min={0} step="1" value={form.baselineQuoteValidityDays} onChange={(e) => setField('baselineQuoteValidityDays', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-baseline-validity-days" />
            </FormField>
            <FormField label="Payment terms baseline">
              <input value={form.baselinePaymentTerms} onChange={(e) => setField('baselinePaymentTerms', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-baseline-payment-terms" />
            </FormField>
            <FormField label="Freight / incoterms baseline">
              <input value={form.baselineFreightTerms} onChange={(e) => setField('baselineFreightTerms', e.target.value)} className={INPUT_CLASS} data-testid="scm-bom-field-baseline-freight-terms" />
            </FormField>
            <FormField label="Notes" className="sm:col-span-2">
              <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} className={cn(INPUT_CLASS, 'min-h-[4rem] resize-y')} data-testid="scm-bom-field-notes" />
            </FormField>
          </FormSection>

          {error && (
            <div className="rounded-sm border border-danger/30 bg-danger/5 px-3 py-2 text-[10px] text-danger" data-testid="scm-bom-edit-error">{error}</div>
          )}
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/10 px-4 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} className="rounded-sm">Cancel</Button>
          <Button type="submit" size="sm" className="rounded-sm" data-testid="scm-bom-edit-submit">{mode === 'add' ? 'Add item' : 'Save changes'}</Button>
        </footer>
      </form>
    </div>
  );
};
